/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import TerminatedAPI from 'api/faber/TerminatedAPI'
import OrbitSession from 'hooks/useCookies'
import { terminatedUsersRowsType } from 'types/api/faber/terminated.type'

function TerminatedIndivEmailDialog({
  customerRow,
  refetchAllUsers,
  setConfirmDialogOpen,
  confirmDialogOpen
}: ISendEmailProps) {
  const faberToken = OrbitSession.get('faberToken')

  const { mutate: indivEmail, isLoading: isSendingIndivEmailLoading } =
    useMutation(TerminatedAPI.sendIndivEmail, {
      onSuccess: () => {
        toast.success('Email has been successfully sent')
        handleConfirmDialogClose()
        refetchAllUsers()
      },
      onError: (err: any) => {
        if (err.response.data.message === 'Phone number was already updated') {
          toast.error(
            'User has already updated phone number before, they are not allowed to update it again.'
          )
          handleConfirmDialogClose()
          refetchAllUsers()
        } else {
          toast.error(
            'There was a problem trying to send the email to the user. Please try again.'
          )
          handleConfirmDialogClose()
          refetchAllUsers()
        }
      }
    })

  // mutation shld always be a verb
  const handleSendEmail = () => {
    indivEmail({
      userId: customerRow.userId,
      faberToken: faberToken
    })
  }

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false)
  }

  return (
    <>
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        sx={{ minWidth: '800px' }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: '-15px',
              mt: '-5px'
            }}
          >
            <Typography
              sx={{
                color: 'black',
                fontSize: '18px',
                fontWeight: '700'
              }}
            >
              Email confirmation
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ p: 2 }}>
            Are you sure you want to send the{' '}
            <em>Update Account Details email</em> to{' '}
            <strong>{customerRow.firstName}</strong>?
          </Typography>
          <Alert severity='info'>
            Please inform customer that the link will be valid for 2 months upon
            sending the email
          </Alert>
          <Alert severity='warning'>
            Please ensure that user has terminated their number with
            Circles.Life
          </Alert>
          <Stack
            justifyContent={'space-between'}
            flexDirection={'row'}
            sx={{ mx: 17, mt: 5 }}
          >
            <Button onClick={handleConfirmDialogClose}>Cancel</Button>
            <LoadingButton
              variant='contained'
              component='label'
              onClick={handleSendEmail}
              loading={isSendingIndivEmailLoading}
              loadingIndicator={<CircularProgress />}
            >
              Confirm
            </LoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TerminatedIndivEmailDialog

interface ISendEmailProps {
  customerRow: terminatedUsersRowsType
  refetchAllUsers: () => void
  setConfirmDialogOpen: (value: boolean) => void
  confirmDialogOpen: boolean
}
